import * as React from "react";
import { motion } from "framer-motion";
import gsap from "gsap";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const HighlightText = React.forwardRef(function HighlightText(
  {
    text,
    as: Component = "h1",
    className,
    textClassName,
    highlightClassName,
    duration = 1.5,
    highlightColor = "#d7ff7b",
    ease = "easeInOut",
    organic = true,
    shineOnHover = false,
    shineColor = "rgba(255,255,255,0.6)",
    ...props
  },
  ref
) {
  const containerRef = React.useRef(null);
  const shineRef = React.useRef(null);

  React.useEffect(() => {
    if (!shineOnHover || !containerRef.current || !shineRef.current) return;
    const root = containerRef.current;
    const shine = shineRef.current;

    const onEnter = () => {
      gsap.set(shine, { x: "-120%", opacity: 0.0 });
      gsap.to(shine, { x: "120%", opacity: 1, duration: 0.8, ease: "power2.out" });
    };
    const onLeave = () => {
      gsap.to(shine, { opacity: 0, duration: 0.25, ease: "power1.out" });
    };

    root.addEventListener("mouseenter", onEnter);
    root.addEventListener("mouseleave", onLeave);
    return () => {
      root.removeEventListener("mouseenter", onEnter);
      root.removeEventListener("mouseleave", onLeave);
    };
  }, [shineOnHover]);
  return (
    <div ref={ref} className={cn("text-left", className)} {...props}>
      <Component
        className={cn(
          "font-[700] leading-[1.35] tracking-normal max-w-3xl",
          textClassName
        )}
      >
        <motion.span
          ref={containerRef}
          className={cn(
            "relative inline-block py-[6px] px-[10px] rounded-[10px] mb-[6px]",
            highlightClassName
          )}
          style={{
            backgroundColor: highlightColor,
            transform: organic ? "skewX(-1deg) rotate(-0.5deg)" : undefined,
            boxShadow: organic ? "inset 0 0 0 1px rgba(0,0,0,0.04)" : undefined
          }}
          initial={{ clipPath: "inset(0 100% 0 0)" }}
          animate={{ clipPath: "inset(0 0% 0 0)" }}
          transition={{ duration, ease }}
        >
          {shineOnHover ? (
            <span
              ref={shineRef}
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-0 w-1/3 opacity-0"
              style={{
                background:
                  `linear-gradient(100deg, rgba(255,255,255,0) 0%, ${shineColor} 50%, rgba(255,255,255,0) 100%)`,
                filter: "blur(1px)",
                mixBlendMode: "screen"
              }}
            />
          ) : null}
          {/* Organic noise overlay */}
          {organic ? (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-[10px]"
              style={{
                backgroundImage:
                  "radial-gradient(1px 1px at 10% 20%, rgba(0,0,0,0.06) 20%, transparent 21%), radial-gradient(1px 1px at 30% 80%, rgba(0,0,0,0.05) 20%, transparent 21%), radial-gradient(1px 1px at 80% 30%, rgba(0,0,0,0.05) 20%, transparent 21%)",
                opacity: 0.25
              }}
            />
          ) : null}

          {/* Hand-drawn underline (scribble) */}
          {organic ? (
            <motion.svg
              aria-hidden
              width="100%"
              height="14"
              viewBox="0 0 100 14"
              className="absolute left-[6px] right-[6px] bottom-0"
              initial={{ strokeDasharray: 120, strokeDashoffset: 120, opacity: 0 }}
              animate={{ strokeDashoffset: 0, opacity: 1 }}
              transition={{ duration: duration * 0.9, ease }}
            >
              <path
                d="M2 7 C 15 12, 30 2, 50 9 S 85 12, 98 6"
                fill="none"
                stroke="rgba(0,0,0,0.25)"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            </motion.svg>
          ) : null}

          {/* Actual text */}
          <span className="relative z-10">
            {text.split("\n").map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 ? <br /> : null}
                {line}
              </React.Fragment>
            ))}
          </span>
        </motion.span>
      </Component>
    </div>
  );
});

export { HighlightText };